// MUST run before "@web-components/cart" — wraps cart links with drawer-trigger
// while <cart-data-conditions> is still an undefined custom element, so the
// cloned link keeps its original <template conditions> children intact. See
// the file header for the full reasoning.
import '@/lib/wrap-cart-links';
import '@web-components/cart';
import '@web-components/drawer';
import '@web-components/accordion';
import '@web-components/quantity-input';
import '@web-components/cart-recommendations';
import '@web-components/bulk-pricing';
import '@web-components/keen-slider';
import '@web-components/keen-slider-navigation-arrows';
import '@web-components/keen-slider-navigation-arrows.css';

import './sidecart.css';

import AjaxCart from '@brytdesigns/shopify-ajax-cart';
import { Effect, Queue, Option, Fiber, Ref, Duration } from 'effect';
import { animate, prefersReducedMotion } from 'motion';
import { CartRuntime } from '@/lib/effect/cart';
import { AddToCartEvents, type AddToCartEvent } from '@/lib/effect/cart';

const onLatestSuccess = (_e: Extract<AddToCartEvent, { _tag: 'success' }>) =>
  Effect.sync(() => {
    const sidecart = document.getElementById('ajaxCartDrawer');
    if (!sidecart) return;
    sidecart.setAttribute('is-open', 'true');
  });

export const startDebouncedLatestSuccessListener = (q: Queue.Dequeue<AddToCartEvent>, millis = 150) =>
  Effect.scoped(
    Effect.gen(function* () {
      const latestSuccessRef = yield* Ref.make<Option.Option<Extract<AddToCartEvent, { _tag: 'success' }>>>(
        Option.none(),
      );

      // holds the currently scheduled debounce fiber (if any)
      const debounceFiberRef = yield* Ref.make<Option.Option<Fiber.RuntimeFiber<void>>>(Option.none());

      const scheduleDebouncedFire = Effect.gen(function* () {
        // cancel prior timer
        const prev = yield* Ref.get(debounceFiberRef);
        if (Option.isSome(prev)) {
          yield* Fiber.interrupt(prev.value);
        }

        // start a new timer
        const f = yield* Effect.fork(
          Effect.sleep(Duration.millis(millis)).pipe(
            Effect.zipRight(
              Ref.get(latestSuccessRef).pipe(
                Effect.flatMap(
                  Option.match({
                    onNone: () => Effect.void,
                    onSome: onLatestSuccess,
                  }),
                ),
              ),
            ),
          ),
        );

        yield* Ref.set(debounceFiberRef, Option.some(f));
      });

      yield* Queue.take(q).pipe(
        Effect.flatMap((ev) => {
          if (ev._tag === 'success') {
            return Ref.set(latestSuccessRef, Option.some(ev)).pipe(Effect.zipRight(scheduleDebouncedFire));
          }
          return Effect.void;
        }),
        Effect.forever,
      );
    }),
  );

// `addDrawerTriggers` used to live here, wrapping cart links with
// <drawer-trigger> at runtime via cloneNode. That ran on DOMContentLoaded —
// AFTER cart-data-conditions had already mutated its children — so the cloned
// link's <cart-data-conditions> lost its template children and the cart-count
// badge in the header broke. The wrapping has been moved to
// src/lib/wrap-cart-links.ts and is imported as the FIRST line of cart.ts so
// it runs before any cart custom element is registered. See that file's
// header for the full rationale.

const updateShippingProgress = (totalPrice: number) => {
  const elements = document.querySelectorAll<HTMLElement>('SIDECART-SHIPPING-PROGRESS');
  elements.forEach((el) => {
    const threshold = Number(el.dataset.thresholdCents ?? 0);
    if (!Number.isFinite(threshold) || threshold <= 0) return;

    const qualifies = totalPrice >= threshold;
    const percent = Math.min(100, (totalPrice / threshold) * 100);

    el.dataset.qualifies = String(qualifies);

    const bar = el.querySelector<HTMLElement>('[data-progress]');
    if (bar) bar.style.width = `${percent}%`;

    const message = el.querySelector<HTMLElement>('[data-message]');
    if (!message) return;

    if (qualifies) {
      message.innerHTML = el.dataset.templateQualifies ?? message.innerHTML;
      return;
    }

    const remaining = window.Shopify?.formatMoney?.(Math.max(0, threshold - totalPrice));
    const template = el.dataset.templateRemaining ?? '';
    if (remaining && template) {
      message.innerHTML = template.replace('__AMOUNT__', remaining);
    }
  });
};

const startShippingProgressListener = Effect.sync(() => {
  const initial = AjaxCart.data;
  if (initial?.total_price != null) {
    updateShippingProgress(initial.total_price);
  }
  return AjaxCart.subscribe(
    (cart) => cart?.total_price ?? 0,
    (totalPrice) => updateShippingProgress(totalPrice),
  );
}).pipe(Effect.flatMap((unsubscribe) => Effect.addFinalizer(() => Effect.sync(() => unsubscribe()))));

const main = Effect.gen(function* () {
  const events = yield* AddToCartEvents;
  const sub = yield* events.subscribe;
  yield* Effect.fork(startDebouncedLatestSuccessListener(sub, 1000));
  yield* startShippingProgressListener;
  yield* Effect.never;
}).pipe(Effect.scoped);

const ITEM_EXIT_DURATION = 0.3;
const ITEM_EXIT_EASE: [number, number, number, number] = [0.16, 1, 0.3, 1];

const animateItemExit = (item: HTMLElement) => {
  item.setAttribute('data-leaving', '');
  if (prefersReducedMotion.current) return Promise.resolve();
  const controls = animate(
    item,
    {
      opacity: [1, 0],
      transform: ['translateX(0)', 'translateX(20%)'],
      marginTop: '0px',
      marginBottom: '0px',
    },
    { duration: ITEM_EXIT_DURATION, ease: ITEM_EXIT_EASE },
  );
  return controls.finished.catch(() => {});
};

const wireSidecartExitAnimation = () => {
  const drawer = document.getElementById('ajaxCartDrawer');
  if (!drawer) return;
  drawer.addEventListener(
    'click',
    (event) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      const listener = target.closest('cart-data-listener[method="removeItem"]') as HTMLElement | null;
      if (!listener) return;

      if (listener.dataset.sidecartBypass) {
        delete listener.dataset.sidecartBypass;
        return;
      }

      const item = listener.closest('cart-data-array-item') as HTMLElement | null;
      if (!item || item.hasAttribute('data-leaving')) return;

      event.stopImmediatePropagation();
      event.preventDefault();

      animateItemExit(item).finally(() => {
        listener.dataset.sidecartBypass = '1';
        listener.click();
      });
    },
    true,
  );
};

document.addEventListener('DOMContentLoaded', () => {
  CartRuntime.runFork(main);
  wireSidecartExitAnimation();
});
